import React, { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Flex,
  Container,
  Input,
  InputGroup,
  InputRightElement,
  Button,
  Center,
  Text,
} from "@chakra-ui/react";
import AdminDataTable from "components/AdminDataTable/AdminDataTable";
import { AiOutlineSearch } from "react-icons/ai";
import { userAnalyticRequest } from "services/request";
import { QUERY_KEYS } from "utils/constants";
import dayjs from "dayjs";
import Paginator from "components/Paginator";
import { useQuery } from "react-query";
import axiosInstance from "services/httpService";
import { ArrowForwardIcon } from "@chakra-ui/icons";

const SinglePurchases = () => {
  const history = useNavigate();
  const [searchParams] = useSearchParams();
  const [pageSize, setPageSize] = React.useState(10);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [search, setSearch] = React.useState("");
  const [dateIndex, setDateIndex] = React.useState(0);
  const [dateFilter, setDateFilter] = React.useState("allTime");
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");

  const makeAPICall = async ({ queryKey }) => {
    const page = queryKey[1];
    const size = queryKey[2];
    const search = queryKey[3];
    const date = queryKey[4];
    const url =
      userAnalyticRequest.SINGLE_PURCHASES +
      `?page=${page}&size=${size}&search=${search}&date=${date}`;
    const res = await axiosInstance.get(url);
    return res.data;
  };

  const { data: purchaseDetails = {}, isLoading } = useQuery(
    [QUERY_KEYS.SINGLE_PURCHASES, currentPage, pageSize, search, dateFilter],
    makeAPICall,
  );

  useEffect(() => {
    if (startDate.length === 10 && endDate.length === 10) {
      const concatenatedDate = startDate + "," + endDate;
      setDateFilter(concatenatedDate);
      setDateIndex(10);
      handleDateFilter(dateFilter);
    }
  }, [startDate, endDate]);

  const handlePageChange = (newPage: number) => {
    searchParams.set("page", String(newPage));
  };

  const handleSizeChange = (newSize) => {
    searchParams.set("size", String(newSize));
  };

  const handleSearch = (newSearch) => {
    searchParams.set("search", String(newSearch));
  };

  const handleDateFilter = (newDateRange) => {
    searchParams.set("date", String(newDateRange));
  };

  const { paginationMeta, summary } = purchaseDetails;
  const purchases = purchaseDetails.data;

  if (purchaseDetails?.data?.length === 0 && paginationMeta?.totalPages < currentPage) {
    setCurrentPage(0);
    handlePageChange(currentPage);
  }

  const totalAmountInUSD = useMemo(() => summary?.totalAmountInUSD ?? 0, [summary]);

  const COLUMNS = [
    {
      Header: "Name",
      Cell: function uploaded({ cell }: any) {
        return <div>{cell.row.original?.user?.userName}</div>;
      },
    },
    {
      Header: "Email",
      Cell: function uploaded({ cell }: any) {
        return <div>{cell.row.original?.user?.email}</div>;
      },
    },
    {
      Header: "Script",
      Cell: function uploaded({ cell }: any) {
        return <div>{cell.row.original?.script?.name}</div>;
      },
    },
    {
      Header: "Amount",
      Cell: function uploaded({ cell }: any) {
        const transaction = cell.row.original?.transaction;
        if (transaction == null) {
          return <div>-</div>;
        }
        return (
          <div>
            {new Intl.NumberFormat(`en-${transaction.currency?.slice(0, -1)}`, {
              style: "currency",
              currency: transaction.currency,
            }).format(Number(transaction.amount) / 100)}
          </div>
        );
      },
    },
    {
      Header: "Downloads",
      accessor: "downloadCount",
    },
    {
      Header: "Purchase Date",
      Cell: function uploaded({ cell }: any) {
        return <div>{dayjs(cell.row.original?.createdAt).format("MMM D YYYY")}</div>;
      },
    },
  ];

  const dateFilterData = [
    { name: "All Time", value: "allTime" },
    { name: "This Year", value: "thisYear" },
    { name: "This Month", value: "thisMonth" },
    { name: "This Week", value: "thisWeek" },
    { name: "Today", value: "today" },
  ];

  return (
    <Container mt="4" maxW="container.xl">
      <Flex justify={"space-between"} mt={"20px"} align="center">
        <Button
          variant="secondary"
          onClick={() => {
            history(-1);
          }}
        >
          Back
        </Button>

        <Box
          display={{
            base: "none",
            lg: "block",
            xl: "block",
          }}
        >
          <InputGroup>
            <InputRightElement pointerEvents="none">
              <AiOutlineSearch color="gray.300" />
            </InputRightElement>
            <Input
              rounded="full"
              type="text"
              placeholder="Search by name or email"
              bg="white"
              onChange={(e) => {
                setSearch(e.target.value);
                handleSearch(search);
              }}
            />
          </InputGroup>
        </Box>
      </Flex>

      <Box mt="2rem" display="flex" alignItems="center" gap={5}>
        <Flex border="1px solid #E0E6DD" display="inline-flex" alignItems="stretch" height="44px">
          {dateFilterData.map((item, index: number) => (
            <Button
              key={index}
              _hover={{ border: "1px solid #005aff" }}
              bg={dateIndex === index ? "#1f74cf" : "white"}
              p="20px 20px"
              mb={{
                base: "10px",
                md: "0",
                lg: "0",
              }}
              cursor={dateIndex === index ? "default" : "pointer"}
              pointerEvents={dateIndex === index ? "none" : "auto"}
              color={dateIndex === index ? "white" : "black"}
              onClick={() => {
                setDateFilter(item.value);
                setDateIndex(index);
                setStartDate("");
                setEndDate("");
                handleDateFilter(dateFilter);
              }}
            >
              {item.name}
            </Button>
          ))}
        </Flex>

        <Flex
          width="19rem"
          gap={2}
          cursor={dateIndex === 10 ? "default" : "pointer"}
          pointerEvents={dateIndex === 10 ? "none" : "auto"}
          color={dateIndex === 10 ? "white" : "black"}
        >
          <Input
            height="42.5px"
            placeholder={"Select Start Date"}
            size="sm"
            color={dateIndex === 10 ? "white" : "black"}
            bg={dateIndex === 10 ? "#1f74cf" : "white"}
            borderRadius={dateIndex === 10 ? "md" : ""}
            type={"date"}
            onChange={(e) => {
              setStartDate(e.target.value);
            }}
          />
          <Center color="black" pointerEvents="none">
            <ArrowForwardIcon pointerEvents="none" />
          </Center>
          <Input
            height="42.5px"
            placeholder={"Select End Date"}
            size="sm"
            color={dateIndex === 10 ? "white" : "black"}
            bg={dateIndex === 10 ? "#1f74cf" : "white"}
            borderRadius={dateIndex === 10 ? "md" : ""}
            type={"date"}
            onChange={(e) => {
              setEndDate(e.target.value);
            }}
          />
        </Flex>
      </Box>

      <Flex mt="1.5rem" gap={8}>
        <Box>
          <Text fontSize="12px" color="#bdc2ba">
            Total Purchases
          </Text>
          <Text fontSize="20px" fontWeight="800">
            {summary?.totalPurchases ?? 0}
          </Text>
        </Box>
        <Box>
          <Text fontSize="12px" color="#bdc2ba">
            Total Revenue (USD)
          </Text>
          <Text fontSize="20px" fontWeight="800">
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(Number(totalAmountInUSD))}
          </Text>
        </Box>
      </Flex>

      <Box p={"0 10px"} mt="2">
        <AdminDataTable
          ariaLabel="Single purchases"
          columns={COLUMNS}
          data={purchases}
          loading={isLoading}
          showRowNumbers
          startIndex={pageSize * currentPage + 1}
        />
      </Box>
      <Center>
        {!purchases || purchases.length === 0 ? (
          ""
        ) : (
          <Paginator
            pageCount={paginationMeta?.totalPages}
            onPageChange={(page) => {
              setCurrentPage(Number(page.selected));
              handlePageChange(currentPage);
            }}
            pageNo={paginationMeta?.currentPage}
            pageSize={pageSize}
            onSizeChange={(selectedPage) => {
              setPageSize(Number(selectedPage));
              handleSizeChange(pageSize);
            }}
          />
        )}
      </Center>
    </Container>
  );
};

export default SinglePurchases;
